import { Stage as KonvaStage } from "konva/lib/Stage";
import React from "react";
import { useWindowSize } from "../../../hooks/useWindowSize";
import { useMapCanvas } from "../contexts/MapCanvasContext";

interface CanvasMiniMapProps {
  stageRef: React.RefObject<KonvaStage | null>;
  width?: number;
  height?: number;
}

interface PositionState {
  prevPosition: { x: number; y: number };
  prevScale: number;
}

export const CanvasMiniMap: React.FC<CanvasMiniMapProps> = ({
  stageRef,
  width: defaultWidth = 200,
  height: defaultHeight = 150
}) => {
  const viewPortRef = React.useRef<HTMLDivElement>(null);
  const { width: windowWidth, height: windowHeight } = useWindowSize();
  const [imageSrc, setImageSrc] = React.useState<string | null>(null);
  const [viewport, setViewport] = React.useState({
    left: 0,
    top: 0,
    width: 50,
    height: 50
  });
  const minimapRef = stageRef;

  let miniMapWidth = defaultWidth;
  let miniMapHeight = defaultHeight;

  const { editingZoneWidth, editingZoneHeight } = useMapCanvas();

  if (editingZoneWidth > editingZoneHeight) {
    miniMapHeight = (editingZoneHeight / editingZoneWidth) * defaultWidth;
  } else if (editingZoneWidth < editingZoneHeight) {
    miniMapWidth = (editingZoneWidth / editingZoneHeight) * defaultHeight;
  }

  const stageWidth = minimapRef.current?.width() || 0;
  const stageHeight = minimapRef.current?.height() || 0;
  const miniMapInHeight = stageHeight < stageWidth;

  const centerStage = React.useCallback((): PositionState | void => {
    if (!minimapRef.current) return;

    const stage = minimapRef.current;
    const prevPosition = stage.position();
    const prevScale = stage.scaleX();

    const scale = Math.min(
      windowWidth / editingZoneWidth,
      windowHeight / editingZoneHeight
    );
    stage.scale({ x: scale, y: scale });

    const editingZoneCenter = {
      x: editingZoneWidth / 2,
      y: editingZoneHeight / 2
    };

    const newStagePos = {
      x: windowWidth / 2 - editingZoneCenter.x * scale,
      y: windowHeight / 2 - editingZoneCenter.y * scale
    };

    stage.position(newStagePos);
    stage.batchDraw();

    return { prevPosition, prevScale };
  }, [
    windowWidth,
    windowHeight,
    editingZoneWidth,
    editingZoneHeight,
    stageWidth,
    stageHeight,
    miniMapHeight,
    miniMapWidth,
    miniMapInHeight
  ]);

  // While a capture runs (and until its own restore-draw has flushed),
  // ignore the draw/contentChange events it produces itself — otherwise
  // every capture schedules the next one, forever.
  const isCapturingRef = React.useRef(false);
  const captureTimeoutRef = React.useRef<number | null>(null);

  const captureStageImage = React.useCallback(() => {
    if (!minimapRef.current) return;

    isCapturingRef.current = true;
    const stage = minimapRef.current;
    const previousState = centerStage();

    let cropX = 0;
    let cropY = 0;

    if (stageHeight < stageWidth) {
      cropX = stageWidth / 2 - (editingZoneWidth * stage.scaleX()) / 2;
    } else {
      cropY = stageHeight / 2 - (editingZoneHeight * stage.scaleY()) / 2;
    }

    const dataURL = stage.toDataURL({
      x: cropX,
      y: cropY
    });
    setImageSrc(dataURL);

    if (previousState) {
      stage.position(previousState.prevPosition);
      stage.scale({ x: previousState.prevScale, y: previousState.prevScale });
      stage.batchDraw();
    }

    // batchDraw flushes on the next animation frame — release the guard
    // one frame after that so the restore draw can't re-trigger a capture.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        isCapturingRef.current = false;
      });
    });
  }, [centerStage]);

  // Rasterizing the whole stage (toDataURL) is expensive on big maps, and
  // the triggers arrive in bursts — one contentChange per icon image load
  // during open, one draw per edit. Debounce so a burst costs one capture.
  const scheduleCapture = React.useCallback(() => {
    if (isCapturingRef.current) return;
    if (captureTimeoutRef.current !== null) {
      window.clearTimeout(captureTimeoutRef.current);
    }
    captureTimeoutRef.current = window.setTimeout(() => {
      captureTimeoutRef.current = null;
      captureStageImage();
    }, 300);
  }, [captureStageImage]);

  const updateViewport = React.useCallback(
    (setState = true) => {
      if (!minimapRef.current || !stageWidth || !stageHeight) return;

      const stage = minimapRef.current;
      const zoom = stage.scaleX();
      const stageX = stage.x();
      const stageY = stage.y();

      const viewPortStyles = {
        left: -(stageX / zoom) * (miniMapWidth / editingZoneWidth),
        top: -(stageY / zoom) * (miniMapHeight / editingZoneHeight),
        width: (stageWidth / zoom) * (miniMapWidth / editingZoneWidth),
        height: (stageHeight / zoom) * (miniMapHeight / editingZoneHeight)
      };

      if (!setState && viewPortRef.current) {
        viewPortRef.current.style.left = `${viewPortStyles.left}px`;
        viewPortRef.current.style.top = `${viewPortStyles.top}px`;
        viewPortRef.current.style.width = `${viewPortStyles.width}px`;
        viewPortRef.current.style.height = `${viewPortStyles.height}px`;

        return;
      }

      setViewport(viewPortStyles);
    },
    [
      editingZoneWidth,
      editingZoneHeight,
      stageWidth,
      stageHeight,
      miniMapWidth,
      miniMapHeight,
      viewPortRef.current
    ]
  );

  React.useEffect(() => {
    if (!minimapRef.current || !stageWidth || !stageHeight) return;

    const onUpdateViewPortWithoutState = () => {
      updateViewport(false);
    };

    const onUpdateViewPort = () => {
      updateViewport();
    };

    const stage = minimapRef.current;
    stage.on("draw", scheduleCapture);
    stage.on("dragmove", onUpdateViewPortWithoutState);
    stage.on("dragend", onUpdateViewPort);
    stage.on("wheel", onUpdateViewPort);
    stage.on("contentChange", scheduleCapture);

    scheduleCapture();
    updateViewport();

    return () => {
      stage.off("draw", scheduleCapture);
      stage.off("dragmove", onUpdateViewPortWithoutState);
      stage.off("wheel", onUpdateViewPort);
      stage.off("dragend", onUpdateViewPort);
      stage.off("contentChange", scheduleCapture);
      if (captureTimeoutRef.current !== null) {
        window.clearTimeout(captureTimeoutRef.current);
        captureTimeoutRef.current = null;
      }
    };
  }, [scheduleCapture, updateViewport, stageWidth, stageHeight]);

  const handleMinimapMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    const stage = minimapRef.current;
    if (!stage) return;

    const rect = event.currentTarget.getBoundingClientRect();
    const startX = event.clientX - rect.left;
    const startY = event.clientY - rect.top;

    const scaleX = editingZoneWidth / miniMapWidth;
    const scaleY = editingZoneHeight / miniMapHeight;

    const stageScale = stage.scaleX();

    const targetX = windowWidth / 2 - startX * scaleX * stageScale;
    const targetY = windowHeight / 2 - startY * scaleY * stageScale;

    stage.position({ x: targetX, y: targetY });
    stage.batchDraw();
    updateViewport();

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const moveX = moveEvent.clientX - rect.left;
      const moveY = moveEvent.clientY - rect.top;

      const dx = (moveX - startX) * scaleX * stageScale;
      const dy = (moveY - startY) * scaleY * stageScale;

      stage.position({ x: targetX - dx, y: targetY - dy });
      stage.batchDraw();
      updateViewport();
    };

    const handleMouseUp = () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
  };

  return (
    <div
      style={{
        position: "absolute",
        bottom: "20px",
        right: "20px",
        width: `${miniMapWidth}px`,
        height: `${miniMapHeight}px`,
        backgroundColor: "rgba(255, 255, 255, 0.9)",
        border: "1px solid rgba(0, 0, 0, 0.2)",
        borderRadius: "8px",
        overflow: "hidden"
      }}
    >
      {imageSrc ? (
        <img
          src={imageSrc}
          alt="Minimap Snapshot"
          style={{
            height: miniMapInHeight ? miniMapHeight : "auto",
            width: miniMapInHeight ? "auto" : miniMapWidth,
            position: "absolute",
            top: 0,
            left: 0,
            maxWidth: "none"
          }}
        />
      ) : undefined}
      <div
        ref={viewPortRef}
        style={{
          position: "absolute",
          backgroundColor: "rgba(0, 0, 0, 0.3)",
          border: "1px solid black",
          pointerEvents: "none",
          ...viewport
        }}
      />
      <div
        className={"absolute inset-0"}
        onMouseDown={handleMinimapMouseDown}
      />
    </div>
  );
};
